import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Search,
  Plus,
  Edit,
  Users,
  Calendar,
  Filter
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { Batch } from '../../types/batch';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import StatusBadge from '../../components/ui/StatusBadge';
import { collection, getDocs, getFirestore } from 'firebase/firestore'; // Import Firestore methods  
import { app } from "../../firebase"; // Import Firestore configuration  

const AllBatches: React.FC = () => {
  const [batches, setBatches] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const { toast } = useToast();
  const db = getFirestore(app); // Initialize Firestore  
  useEffect(() => {
    const fetchBatches = async () => {
      
      const batchesCollection = collection(db, 'courseBatches'); // Reference the Firestore collection  

      try {
        const snapshot = await getDocs(batchesCollection);
        const fetchedBatches = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as any[];

        setBatches(fetchedBatches); // Update the state with fetched batches  
      } catch (error) {
        console.error('Error fetching batches:', error);
        toast({
          title: "Error fetching batches",
          description: "There was an issue loading the batches.",
        });
      }
    };

    fetchBatches(); // Call the function to fetch batches  
  }, [toast]); // Run effect on toast change (optional)  

  const filteredBatches = batches.filter(batch => {
    const matchesSearch = batch?.batchName?.toLowerCase().includes(searchQuery?.toLowerCase()) ||
      batch?.description?.toLowerCase().includes(searchQuery?.toLowerCase());

    const matchesStatus = statusFilter ? batch?.status === statusFilter : true;

    return matchesSearch && matchesStatus;
  });

  const resetFilters = () => {
    setSearchQuery('');
    setStatusFilter(null);
  };

  const handleFilterByStatus = (status: string | null) => {
    setStatusFilter(status);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="p-6 h-full overflow-y-auto bg-gray-50">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Batches</h1>
          <p className="text-gray-500 mt-1">Manage your training batches</p>
        </div>
        <Link to="/batches/create">
          <Button className="bg-bytesplash-blue hover:bg-bytesplash-light-blue">
            <Plus className="mr-2 h-4 w-4" /> Create Batch
          </Button>
        </Link>
      </div>

      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row justify-between gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search batches..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-full"
              />
            </div>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" className="flex gap-2">
                    <Filter className="h-4 w-4" />
                    <span>Status</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleFilterByStatus(null)}>
                    All
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFilterByStatus('active')}>
                    Active
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFilterByStatus('inactive')}>
                    Inactive
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleFilterByStatus('suspended')}>
                    Suspended
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              {(searchQuery || statusFilter) && (
                <Button variant="ghost" onClick={resetFilters}>
                  Reset
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="overflow-hidden">
        <div className="relative rounded-md overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[300px]">Batch Name</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>Students</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredBatches?.length > 0 ? (
                filteredBatches?.map((batch) => (
                  <TableRow key={batch.id}>
                    <TableCell className="font-medium">
                      <div>
                        <div className="font-semibold text-gray-800">{batch.batchName}</div>
                        <div className="text-sm text-gray-500 mt-1">{batch.description}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Calendar className="mr-2 h-4 w-4 text-gray-400" />
                        <div>
                          <div className="text-sm">{formatDate(batch.startDate)}</div>
                          <div className="text-xs text-gray-500">to {formatDate(batch.endDate)}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center">
                        <Users className="mr-2 h-4 w-4 text-gray-400" />
                        <span>{batch.students?.length} students</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={batch.status} />
                    </TableCell>
                    <TableCell className="text-right">
                      <Link to={`/batches/edit/${batch.id}`}>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} className="h-32 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-500">
                      <p>No batches found</p>
                      <Button
                        variant="link"
                        className="mt-2"
                        onClick={resetFilters}
                      >
                        Reset filters
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>
    </div>
  );
};

export default AllBatches;  